(function () {
  if (customElements.get('ofenerlebnis-filter-engine')) return;

  const HASH_PREFIX = '#oe?';

  class OfenerlebnisFilterEngine extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      const connectionSignal = this.signal;
      this.applyTimer = null;
      this.drawerOpener = null;
      this.baseUrl = this.dataset.baseUrl || window.location.pathname;
      this.pageSize = Number.parseInt(this.dataset.pageSize, 10) || 24;
      this.defaultSort = this.dataset.defaultSort || 'manual';
      this.context = this.dataset.context || 'collection';
      this.labels = new Map();
      this.renderableListValues = new Map();
      this.cardCache = new Map();
      this.cardPromises = new Map();
      this.navigationPromises = new Map();
      this.renderToken = 0;
      this.unindexedHandles = [];
      this.verifiedPowerSelectionActive = false;
      this.gridClassName = document.querySelector('#ProductGridContainer #product-grid')?.className ||
        'grid product-grid grid--2-col-tablet-down grid--3-col-desktop';
      this.captureStandardCards(document);
      this.serverGridHtml = document.querySelector('#ProductGridContainer')?.innerHTML || '';
      this.serverPageKey = window.location.pathname + window.location.search;
      this.nativeGridVisible = true;
      this.serverProductCount = Number(document.querySelector('#ProductCount')?.dataset.productCount);
      this.numberFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });
      this.moneyFormatter = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      });

      // Drawer controls must work even while a large index is loading
      // or when catalog changes make the index contract fail.
      this.bindUiEvents();

      try {
        const registry = window.OeFilterIndexes?.[this.dataset.profile];
        const collectionHandle = new URL(this.baseUrl, window.location.origin).pathname.split('/').filter(Boolean).pop();
        const sourceIndex = registry?.collections?.[collectionHandle] || registry;
        const index = this.ensureImplicitProfileDimensions(sourceIndex, collectionHandle);
        if (!window.OeIndependentFilterDataAdapter) throw new Error('Filteradapter fehlt.');
        if (this.context === 'collection' && index?.profile === this.dataset.profile &&
            index.collection?.handle === collectionHandle && Array.isArray(index.products)) {
          this.adapter = new window.OeIndependentFilterDataAdapter(index);
          this.collectLabels();
          this.renderControls();
          this.orderPriceGroup();
          this.state = this.readStateFromLocation();
          this.syncForms();
          this.renderActiveFilters();
          this.dataset.uiReady = 'true';
        }
        const preparedIndex = this.context === 'search'
          ? await this.prepareSearchIndex(index)
          : await this.prepareCollectionIndex(index);
        if (connectionSignal.aborted || !this.isConnected) return;
        const scopedIndex = this.applyVerifiedPowerSelection(preparedIndex);
        this.adapter = new window.OeIndependentFilterDataAdapter(scopedIndex);
        this.assertProfileContract();
        this.collectLabels();
        this.renderControls();
        this.orderPriceGroup();
        this.bindEvents();
        this.state = this.readStateFromLocation();
        await this.render({ updateHistory: false, announce: false });
        this.dataset.adapterReady = 'true';
        this.dataset.hydrationState = 'ready';
        this.setAttribute('aria-busy', 'false');
      } catch (error) {
        if (!connectionSignal.aborted && this.isConnected) this.failSafely(error);
      }
    }

    ensureImplicitProfileDimensions(index, collectionHandle) {
      if (
        this.dataset.profile !== 'KAMIN_UND_OEFEN_KATALOG' ||
        collectionHandle !== 'kamin' ||
        index?.dimensions?.energieeffizienz
      ) return index;
      const energyValues = [...new Set(index.products.flatMap((product) => product.p?.ee || []))];
      return {
        ...index,
        dimensions: {
          ...index.dimensions,
          energieeffizienz: { s: 'p', k: 'ee', t: 'list' },
        },
        values: {
          ...index.values,
          energieeffizienz: energyValues,
        },
      };
    }

    disconnectedCallback() {
      window.clearTimeout(this.applyTimer);
      this.controller?.abort();
      document.body.classList.remove('oe-filter-drawer-open');
      this.initialized = false;
    }

    applyVerifiedPowerSelection(index) {
      const url = new URL(window.location.href);
      if (this.dataset.profile !== 'KAMINOFEN' || url.searchParams.get('oe_power_verified') !== '1') return index;
      this.verifiedPowerSelectionActive = true;
      const minimum = Number(url.searchParams.get('oe_power_min'));
      const maximum = Number(url.searchParams.get('oe_power_max'));
      if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || minimum > maximum) {
        return { ...index, products: [] };
      }
      const products = index.products
        .map((product) => ({
          ...product,
          v: product.v.filter((variant) => (
            variant.pwt === 1 && Number(variant.pw) >= minimum && Number(variant.pw) <= maximum
          )),
        }))
        .filter((product) => product.v.length > 0);
      return { ...index, products };
    }

    assertProfileContract() {
      const profile = this.adapter.getProfile();
      const groupHandles = [...this.querySelectorAll('[data-oe-filter-form="desktop"] [data-oe-filter-group]')].map(
        (group) => group.dataset.filterHandle,
      );
      const indexHandles = Object.keys(this.adapter.dimensions);
      if (profile.key !== this.dataset.profile) throw new Error('Profil und Filterindex stimmen nicht überein.');
      if (groupHandles.length !== indexHandles.length || groupHandles.some((handle) => !indexHandles.includes(handle))) {
        throw new Error('Filterprofil und Indexdimensionen stimmen nicht überein.');
      }
      const contextHandle = new URL(this.baseUrl, window.location.origin).pathname.split('/').filter(Boolean).pop();
      if (profile.collection.handle !== contextHandle) {
        throw new Error('Filterindex gehört nicht zur aktuellen Collection.');
      }
      const serverCount = Number(document.querySelector('#ProductCount')?.dataset.totalCount);
      if (this.context !== 'search' && serverCount && serverCount !== profile.collection.productCount + this.unindexedHandles.length) {
        throw new Error('Filterindex und Collection-Umfang stimmen nicht überein.');
      }
    }

    async fetchNavigation(sortKey = 'manual') {
      if (this.navigationPromises.has(sortKey)) return this.navigationPromises.get(sortKey);
      const request = (async () => {
        const sorts = { manual: 'MANUAL', 'best-selling': 'BEST_SELLING', 'title-ascending': 'TITLE',
          'title-descending': 'TITLE', 'price-ascending': 'PRICE', 'price-descending': 'PRICE',
          'created-ascending': 'CREATED', 'created-descending': 'CREATED' };
        const query = 'query OeCollectionOrder($handle:String!,$after:String,$sort:ProductCollectionSortKeys!,$reverse:Boolean!){collection(handle:$handle){handle products(first:250,after:$after,sortKey:$sort,reverse:$reverse){nodes{handle}pageInfo{hasNextPage endCursor}}}}';
        const handles = [];
        let after = null, more = true;
        while (more) {
          // Shopify's public tokenless API: only current membership/order, no card
          // HTML, variants, customer data, credentials or external service.
          const response = await fetch('/api/2026-10/graphql.json', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: this.signal,
            body: JSON.stringify({ query, variables: { handle: this.dataset.collectionHandle,
              after, sort: sorts[sortKey] || 'MANUAL', reverse: sortKey.endsWith('-descending') } }),
          });
          if (!response.ok) throw new Error('Collection-Reihenfolge: HTTP ' + response.status);
          const payload = await response.json(), collection = payload.data?.collection;
          if (payload.errors?.length || collection?.handle !== this.dataset.collectionHandle) {
            throw new Error('Shopify-Collection-Reihenfolge ist nicht verfügbar.');
          }
          handles.push(...collection.products.nodes.map(product => product.handle));
          more = collection.products.pageInfo.hasNextPage;
          after = collection.products.pageInfo.endCursor;
          if (more && !after) throw new Error('Collection-Fortsetzung fehlt.');
        }
        const total = Number(document.querySelector('#ProductCount')?.dataset.totalCount);
        if (handles.length !== total || new Set(handles).size !== total) {
          throw new Error('Collection wurde während des Abgleichs geändert. Bitte Seite neu laden.');
        }
        return handles;
      })().catch(error => { this.navigationPromises.delete(sortKey); throw error; });
      this.navigationPromises.set(sortKey, request);
      return request;
    }

    async prepareCollectionIndex(index) {
      if (!Array.isArray(index?.products)) throw new Error('Collection-Filterindex fehlt.');
      const handles = await this.fetchNavigation('manual');
      const byHandle = new Map(index.products.map(product => [product.h, product]));
      if (byHandle.size !== index.products.length) throw new Error('Doppelte Produkte im Filterindex.');
      const knownHandles = handles.filter(handle => byHandle.has(handle));
      this.unindexedHandles = handles.filter(handle => !byHandle.has(handle));
      this.dataset.indexState = this.unindexedHandles.length || byHandle.size !== handles.length ? 'partial' : 'current';
      this.dataset.unindexedCount = String(this.unindexedHandles.length);
      const knownSet = new Set(knownHandles), products = knownHandles.map(handle => byHandle.get(handle));
      const sortRanks = Object.fromEntries(Object.entries({ ...index.sortRanks, manual: knownHandles }).map(
        ([key, rank]) => [key, key === 'manual' ? knownHandles : [...rank.filter(handle => knownSet.has(handle)), ...knownHandles.filter(handle => !rank.includes(handle))]],
      ));
      return { ...index, collection: { ...index.collection, productCount: products.length }, products,
        values: this.dataset.indexState === 'partial' ? this.searchIndexValues(products, index.dimensions) : index.values, sortRanks };
    }

    async fetchSearchDocument(page) {
      const url = new URL(this.baseUrl, window.location.origin);
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.signal,
      });
      if (!response.ok) throw new Error(`Suchergebnis-Seite ${page}: HTTP ${response.status}`);
      const incoming = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!incoming.querySelector('[data-oe-search-manifest]')) throw new Error(`Suchmanifest fehlt auf Seite ${page}.`);
      this.captureStandardCards(incoming);
      return incoming;
    }

    searchIndexValues(products, dimensions) {
      const values = {};
      for (const [handle, dimension] of Object.entries(dimensions)) {
        const entries = products.flatMap((product) => {
          const entities = dimension.s === 'p' ? [product.p] : product.v.filter((variant) => variant.a === 1);
          return entities.flatMap((entity) => Array.isArray(entity[dimension.k]) ? entity[dimension.k] : entity[dimension.k] == null ? [] : [entity[dimension.k]]);
        });
        if (dimension.t === 'number') {
          const numbers = entries.map(Number).filter(Number.isFinite);
          values[handle] = numbers.length ? { min: Math.min(...numbers), max: Math.max(...numbers), count: new Set(numbers).size } : { min: 0, max: 0, count: 0 };
        } else {
          values[handle] = [...new Set(entries.filter((value) => typeof value === 'string' && value.trim() !== ''))]
            .sort((left, right) => left.localeCompare(right, 'de', { numeric: true }));
        }
      }
      return values;
    }

    async prepareSearchIndex(index) {
      if (!index?.products || index.profile !== this.dataset.profile) throw new Error('Globaler Suchindex fehlt.');
      const query = new URL(window.location.href).searchParams.get('q')?.trim().toLocaleLowerCase('de') || '';
      const tokens = query.split(/\s+/).filter(Boolean);
      const relevant = (product) => {
        if (!tokens.length) return false;
        const searchable = `${product.t} ${product.h} ${product.vnd || ''}`.toLocaleLowerCase('de');
        return tokens.every((token) => searchable.includes(token));
      };
      const relevantHandles = new Set(index.products.filter(relevant).map((product) => product.h));
      if (!relevantHandles.size) return {
        ...index,
        collection: { ...index.collection, productCount: 0, variantCount: 0 },
        values: this.searchIndexValues([], index.dimensions),
        sortRanks: { ...index.sortRanks, manual: [], 'most-relevant': [], 'best-selling': [] },
        products: [],
      };
      const first = await this.fetchSearchDocument(1);
      const pages = Math.max(Number.parseInt(first.querySelector('[data-oe-search-manifest]')?.dataset.pages, 10) || 1, 1);
      const documents = [first];
      for (let start = 2; start <= pages; start += 6) {
        documents.push(...await Promise.all(Array.from({ length: Math.min(6, pages - start + 1) }, (_, offset) => this.fetchSearchDocument(start + offset))));
      }
      const nativeHandles = documents.flatMap((document) => [...document.querySelectorAll('[data-oe-search-result][data-product-handle]')].map((card) => card.dataset.productHandle));
      const boostHandles = [...first.querySelectorAll('[data-oe-search-boost-card][data-product-handle]')].map((card) => card.dataset.productHandle);
      const orderedHandles = [...new Set([...boostHandles, ...nativeHandles])];
      const byHandle = new Map(index.products.map((product) => [product.h, product]));
      const matchedHandles = orderedHandles.filter((handle) => relevantHandles.has(handle));
      const balancedHandles = this.balanceSearchHandleOrder(matchedHandles, byHandle, query);
      const products = balancedHandles.map((handle) => byHandle.get(handle));
      const ranks = products.map((product) => product.h);
      return {
        ...index,
        collection: { ...index.collection, productCount: products.length, variantCount: products.reduce((sum, product) => sum + product.v.length, 0) },
        values: this.searchIndexValues(products, index.dimensions),
        sortRanks: { ...index.sortRanks, manual: ranks, 'most-relevant': ranks, 'best-selling': ranks },
        products,
      };
    }

    balanceSearchHandleOrder(handles, byHandle, query) {
      const broadQuery = ['kam', 'kami', 'kamin', 'kaminofen', 'pell', 'pellet', 'pelletofen'].includes(query);
      const core = (handle) => {
        if (!broadQuery) return true;
        const title = byHandle.get(handle).t.toLocaleLowerCase('de');
        return query.startsWith('pell')
          ? /^(pelletofen|hybridofen|pellet.kaminofen)/.test(title)
          : /^(kaminofen|kamineinsatz|elektrokamin|ethanol.kamin|bioethanol.kamin|einbau ethanol.kamin)/.test(title);
      };
      const request = (handle) => /data-price-request="true"/.test(this.cardCache.get(handle) || '');
      const priced = [...handles.filter((handle) => core(handle) && !request(handle)), ...handles.filter((handle) => !core(handle) && !request(handle))];
      const requestHandles = [...handles.filter((handle) => core(handle) && request(handle)), ...handles.filter((handle) => !core(handle) && request(handle))];
      const requestGroups = new Map();
      for (const handle of requestHandles) {
        const vendor = byHandle.get(handle).vnd?.toLocaleLowerCase('de') || '';
        if (!requestGroups.has(vendor)) requestGroups.set(vendor, []);
        requestGroups.get(vendor).push(handle);
      }
      const requests = [];
      while (requestGroups.size) {
        for (const [vendor, group] of requestGroups) {
          requests.push(group.shift());
          if (!group.length) requestGroups.delete(vendor);
        }
      }
      const seenVendors = new Set();
      const diversified = [];
      for (const handle of priced) {
        const vendor = byHandle.get(handle).vnd?.toLocaleLowerCase('de');
        if (vendor && !seenVendors.has(vendor)) {
          diversified.push(handle);
          seenVendors.add(vendor);
        }
        if (diversified.length === 3) break;
      }
      priced.splice(0, priced.length, ...diversified, ...priced.filter((handle) => !diversified.includes(handle)));
      const ordered = [];
      while (priced.length >= 5) {
        ordered.push(...priced.splice(0, 5));
        if (requests.length) ordered.push(requests.shift());
      }
      return [...ordered, ...priced, ...requests];
    }

    collectLabels() {
      this.querySelectorAll('[data-oe-filter-form="desktop"] [data-oe-filter-group]').forEach((group) => {
        const label = group.querySelector('[data-oe-filter-label]')?.textContent.trim();
        this.labels.set(group.dataset.filterHandle, label || group.dataset.filterHandle);
      });
    }

    bindEvents() {
      this.addEventListener('change', this.onChange.bind(this), { signal: this.signal });
      this.addEventListener('input', this.onInput.bind(this), { signal: this.signal });
      this.addEventListener('submit', this.onSubmit.bind(this), { signal: this.signal });
      this.addEventListener('keydown', this.onKeydown.bind(this), { signal: this.signal });
      window.addEventListener('popstate', this.onPopstate.bind(this), { signal: this.signal });
    }

    bindUiEvents() {
      document.addEventListener('click', this.onClick.bind(this), { signal: this.signal });
      document.addEventListener('change', this.onDocumentChange.bind(this), { signal: this.signal });
    }

    orderPriceGroup() {
      this.querySelectorAll('[data-oe-filter-form]').forEach((form) => {
        const price = form.querySelector('[data-filter-handle="preis"]');
        const firstGroup = form.querySelector('[data-oe-filter-group]');
        if (price && firstGroup && price !== firstGroup) firstGroup.before(price);
      });
    }

    renderControls() {
      this.querySelectorAll('[data-oe-filter-group]').forEach((group, groupIndex) => {
        const handle = group.dataset.filterHandle;
        const dimension = this.adapter.dimensions[handle];
        const control = group.querySelector('[data-oe-filter-control]');
        if (!dimension || !control) throw new Error('Unbekannte Filterdimension: ' + handle);
        const values = this.adapter.getFilterValues(handle);
        const isAvailable =
          dimension.t === 'number'
            ? values &&
              !Array.isArray(values) &&
              Number(values.count) > 0 &&
              Number.isFinite(Number(values.min)) &&
              Number.isFinite(Number(values.max))
            : Array.isArray(values) && values.length > 0;
        group.hidden = !isAvailable;
        group.dataset.oeFilterAvailable = String(isAvailable);
        if (!isAvailable) {
          control.replaceChildren();
          return;
        }
        const markup = dimension.t === 'number'
          ? this.numericControl(group, handle, groupIndex)
          : this.listControl(group, handle, groupIndex);
        if (dimension.t !== 'number' && this.renderableListValues.get(handle)?.length === 0) {
          group.hidden = true;
          group.dataset.oeFilterAvailable = 'false';
          control.replaceChildren();
          return;
        }
        const currentValues = [...control.querySelectorAll('[data-oe-value]')].map((input) => input.value);
        const nextValues = this.renderableListValues.get(handle) || [];
        const currentRange = control.querySelector('[data-oe-range]');
        const sameList = dimension.t !== 'number' && currentValues.length === nextValues.length &&
          currentValues.every((value, index) => value === nextValues[index]);
        const sameRange = dimension.t === 'number' && currentRange &&
          Number(currentRange.dataset.min) === Number(values.min) &&
          Number(currentRange.dataset.max) === Number(values.max) &&
          Number(currentRange.dataset.factor) === Number(dimension.f || 1) &&
          (currentRange.dataset.hasSlider === 'true') === this.shouldRenderRangeSlider(group, handle, values);
        if (!sameList && !sameRange) control.innerHTML = markup;
        if (handle === 'hersteller' && this.adapter.getFilterValues(handle).length > 10) {
          group.querySelector('[data-oe-filter-search-wrap]')?.removeAttribute('hidden');
        }
      });
      this.querySelectorAll('[data-oe-range]').forEach((range) => this.updateRangeProgress(range));
    }

    listControl(group, handle, groupIndex) {
      const rawValues = this.adapter.getFilterValues(handle);
      const displayType = group.dataset.displayType || 'checkbox';
      const isColor = displayType.includes('color');
      const colorOptions = isColor ? this.validColorOptions(rawValues) : null;
      const values = colorOptions?.values || rawValues;
      this.renderableListValues.set(handle, values);
      if (colorOptions) {
        group.dataset.validColorValues = String(values.length);
        group.dataset.emptyColorValuesSkipped = String(colorOptions.emptySkipped);
        group.dataset.invalidColorValuesSkipped = String(colorOptions.invalidSkipped);
        group.dataset.emptySwatchesRendered = '0';
      }
      const prefix = `${group.closest('form')?.id || 'OeFilter'}-${handle}-${groupIndex}`;
      const items = values
        .map((value, index) => {
          const id = `${prefix}-${index}`;
          const swatch = isColor ? this.adapter.index.swatches[value] : '';
          return `
            <div class="oe-filter-value" data-oe-filter-value data-filter-label="${this.escape(value)}">
              <input id="${this.escape(id)}" type="checkbox" name="oe.${this.escape(handle)}" value="${this.escape(
                value,
              )}"${isColor ? ` aria-label="${this.escape(value)}"` : ''} data-oe-value>
              <label for="${this.escape(id)}"${isColor ? ` title="${this.escape(value)}"` : ''}>
                ${
                  isColor
                    ? `<span class="oe-filter-value__swatch" data-oe-swatch-value="${this.escape(value)}" style="--oe-swatch:${this.escape(swatch)}" aria-hidden="true"></span>`
                    : '<span class="oe-filter-value__check" aria-hidden="true"><svg viewBox="0 0 10 8"><path fill="currentColor" d="M3.5 7.5.5 4.4l1.1-1.1 1.9 2L8.4.4l1.1 1.1z"/></svg></span>'
                }
                <span class="oe-filter-value__label${isColor ? ' visually-hidden' : ''}">${this.escape(value)}</span>
              </label>
            </div>`;
        })
        .join('');
      const valuesId = `${prefix}-values`;
      return `
        <div id="${this.escape(valuesId)}" class="oe-filter-values${isColor ? ' oe-filter-values--swatches' : ''}" data-oe-filter-values${values.length > 7 ? ' data-oe-collapsible="true"' : ''}>
          ${items}
        </div>${values.length > 7 ? `
        <button class="oe-filter-show-more" type="button" data-oe-show-more aria-controls="${this.escape(valuesId)}" aria-expanded="false">Mehr anzeigen</button>` : ''}`;
    }

    validColorOptions(rawValues) {
      const values = [];
      const seen = new Set();
      let emptySkipped = 0;
      let invalidSkipped = 0;
      for (const value of Array.isArray(rawValues) ? rawValues : []) {
        if (typeof value !== 'string' || value.trim() === '') {
          emptySkipped += 1;
          continue;
        }
        const swatch = this.adapter.index.swatches?.[value];
        const isCssColor =
          typeof swatch === 'string' &&
          swatch.trim() !== '' &&
          (typeof window.CSS?.supports === 'function'
            ? window.CSS.supports('color', swatch.trim())
            : /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(swatch.trim()));
        if (!isCssColor || seen.has(value)) {
          invalidSkipped += 1;
          continue;
        }
        seen.add(value);
        values.push(value);
      }
      return { values, emptySkipped, invalidSkipped };
    }

    numericControl(group, handle) {
      const dimension = this.adapter.dimensions[handle];
      const bounds = this.adapter.getNumericBounds(handle);
      const factor = dimension.f || 1;
      const displayMin = bounds.min / factor;
      const displayMax = bounds.max / factor;
      const step = handle === 'preis' ? 1 : 0.1;
      const unit = dimension.du || dimension.u || group.dataset.displayUnit || '';
      const hasSlider = this.shouldRenderRangeSlider(group, handle, bounds);
      const slider = hasSlider
        ? `<div class="oe-range__track" aria-hidden="true"><span data-oe-range-progress></span></div>
          <input class="oe-range__slider oe-range__slider--min" type="range" min="${displayMin}" max="${displayMax}" step="${step}" value="${displayMin}" aria-label="${this.escape(
            this.labels.get(handle),
          )} Mindestwert" data-oe-range-min>
          <input class="oe-range__slider oe-range__slider--max" type="range" min="${displayMin}" max="${displayMax}" step="${step}" value="${displayMax}" aria-label="${this.escape(
            this.labels.get(handle),
          )} Höchstwert" data-oe-range-max>`
        : '';
      return `
        <div class="oe-range" data-oe-range data-handle="${this.escape(handle)}" data-has-slider="${hasSlider}" data-factor="${factor}" data-min="${
          bounds.min
        }" data-max="${bounds.max}" data-display-min="${displayMin}" data-display-max="${displayMax}">
          ${slider}
          <div class="oe-range__inputs">
            <label><span>Von</span><span class="oe-range__input-wrap"><input type="text" inputmode="decimal" autocomplete="off" placeholder="${this.escape(this.formatNumericInput(displayMin, handle))}" data-oe-number-min><span>${this.escape(
              unit,
            )}</span></span></label>
            <span class="oe-range__separator" aria-hidden="true">–</span>
            <label><span>Bis</span><span class="oe-range__input-wrap"><input type="text" inputmode="decimal" autocomplete="off" placeholder="${this.escape(this.formatNumericInput(displayMax, handle))}" data-oe-number-max><span>${this.escape(
              unit,
            )}</span></span></label>
          </div>
        </div>`;
    }

    shouldRenderRangeSlider(group, handle, bounds) {
      const displayType = group.dataset.displayType || '';
      const configuredAsRange = displayType.includes('range_slider');
      const hasUsableSpan = Number(bounds.max) > Number(bounds.min);
      const hasUsefulDistribution = handle === 'preis' || Number(bounds.count) >= 4;
      return handle !== 'preis' && configuredAsRange && hasUsableSpan && hasUsefulDistribution;
    }

    emptyState() {
      return { values: {}, ranges: {} };
    }

    readStateFromLocation() {
      const state = this.emptyState();
      if (!window.location.hash.startsWith(HASH_PREFIX)) return state;
      const params = new URLSearchParams(window.location.hash.slice(HASH_PREFIX.length));
      for (const [key, rawValue] of params.entries()) {
        if (key.startsWith('f.')) {
          const handle = key.slice(2);
          const available = this.renderableListValues.get(handle) || this.adapter.getFilterValues(handle);
          if (!Array.isArray(available) || !available.includes(rawValue)) continue;
          state.values[handle] ||= [];
          if (!state.values[handle].includes(rawValue)) state.values[handle].push(rawValue);
        } else if (key.startsWith('min.') || key.startsWith('max.')) {
          const isMin = key.startsWith('min.');
          const handle = key.slice(4);
          const bounds = this.adapter.getNumericBounds(handle);
          const value = Number(String(rawValue).replace(',', '.'));
          if (!bounds || !Number.isFinite(value)) continue;
          state.ranges[handle] ||= {};
          state.ranges[handle][isMin ? 'min' : 'max'] = Math.min(Math.max(value, bounds.min), bounds.max);
        }
      }
      return this.normalizeState(state);
    }

    stateFromForm(form) {
      const state = this.emptyState();
      form.querySelectorAll('[data-oe-value]:checked').forEach((input) => {
        const handle = input.closest('[data-oe-filter-group]').dataset.filterHandle;
        state.values[handle] ||= [];
        state.values[handle].push(input.value);
      });
      form.querySelectorAll('[data-oe-range]').forEach((range) => {
        const handle = range.dataset.handle;
        const factor = Number(range.dataset.factor) || 1;
        const minInput = range.querySelector('[data-oe-number-min]');
        const maxInput = range.querySelector('[data-oe-number-max]');
        const min = this.parseLocalizedNumber(minInput.value);
        const max = this.parseLocalizedNumber(maxInput.value);
        if (Number.isFinite(min) || Number.isFinite(max)) state.ranges[handle] = {};
        if (Number.isFinite(min)) state.ranges[handle].min = min * factor;
        if (Number.isFinite(max)) state.ranges[handle].max = max * factor;
      });
      return this.normalizeState(state);
    }

    normalizeState(state) {
      for (const [handle, values] of Object.entries(state.values)) {
        const group = this.querySelector(`[data-oe-filter-group][data-filter-handle="${CSS.escape(handle)}"]`);
        const unique = [...new Set(values)];
        state.values[handle] = group?.dataset.multiSelect === 'false' ? unique.slice(-1) : unique;
        if (!state.values[handle].length) delete state.values[handle];
      }
      for (const [handle, range] of Object.entries(state.ranges)) {
        const bounds = this.adapter.getNumericBounds(handle);
        if (!bounds) {
          delete state.ranges[handle];
          continue;
        }
        if (range.min != null) range.min = Math.min(Math.max(Number(range.min), bounds.min), bounds.max);
        if (range.max != null) range.max = Math.min(Math.max(Number(range.max), bounds.min), bounds.max);
        if (range.min != null && range.max != null && range.min > range.max) range.min = range.max;
        if (range.min == null && range.max == null) delete state.ranges[handle];
      }
      return state;
    }

    serializeState(state) {
      const params = new URLSearchParams();
      for (const handle of Object.keys(this.adapter.dimensions)) {
        for (const value of state.values[handle] || []) params.append('f.' + handle, value);
        const range = state.ranges[handle];
        if (range?.min != null) params.set('min.' + handle, this.cleanNumber(range.min));
        if (range?.max != null) params.set('max.' + handle, this.cleanNumber(range.max));
      }
      const serialized = params.toString();
      return serialized ? HASH_PREFIX + serialized : '';
    }

    hasFilterState(state = this.state) {
      return Object.keys(state.values).length > 0 || Object.keys(state.ranges).length > 0;
    }

    async render({ updateHistory = false, replaceHistory = false, announce = true } = {}) {
      const startedAt = performance.now(), token = ++this.renderToken;
      this.dataset.renderState = 'pending';
      this.state = this.normalizeState(this.state || this.emptyState());
      const url = new URL(window.location.href);
      url.hash = this.serializeState(this.state);
      if (updateHistory) {
        if (replaceHistory) window.history.replaceState({ oeFilterState: true }, '', url);
        else window.history.pushState({ oeFilterState: true }, '', url);
      }
      const sortKey = url.searchParams.get('sort_by') || this.defaultSort;
      const constrained = this.adapter.hasAnyConstraint(this.state) || this.verifiedPowerSelectionActive;
      const container = document.querySelector('#ProductGridContainer');
      try {
        let count;
        if (this.context === 'collection' && !constrained) {
          // Unfiltered ordering, unknown/new products, pagination and card HTML belong to Shopify.
          if (this.serverPageKey !== url.pathname + url.search) {
            // pushState/popstate may already have changed the query on this old
            // document. A hash-only replace would not fetch its new native page.
            window.history.replaceState(window.history.state, '', url.pathname + url.search + '#ProductGridContainer');
            window.location.reload();
            return;
          }
          if (!this.nativeGridVisible) {
            container.innerHTML = this.serverGridHtml;
            this.cardCache.clear();
            this.captureStandardCards(container);
          }
          this.nativeGridVisible = true;
          count = this.serverProductCount;
          this.dataset.cardRenderer = 'collection-server';
        } else {
          container?.setAttribute('aria-busy', 'true');
          if (this.context === 'collection' && sortKey === 'best-selling') {
            const handles = await this.fetchNavigation(sortKey);
            if (token !== this.renderToken) return;
            this.adapter.index.sortRanks[sortKey] = handles;
            this.adapter.rankCache.delete(sortKey);
          }
          const matches = this.adapter.applyState(this.state);
          const sorted = this.adapter.sortProducts(matches, this.state, sortKey);
          const requestedPage = Math.max(Number.parseInt(url.searchParams.get('page'), 10) || 1, 1);
          const pages = Math.max(Math.ceil(sorted.length / this.pageSize), 1), page = Math.min(requestedPage, pages);
          if (page !== requestedPage) {
            if (page === 1) url.searchParams.delete('page');
            else url.searchParams.set('page', String(page));
            window.history.replaceState({ oeFilterState: true }, '', url);
          }
          await this.renderGrid(sorted, page, pages, token);
          if (token !== this.renderToken) return;
          count = sorted.length;
          this.nativeGridVisible = false;
        }
        this.syncForms();
        this.updateCounts(count);
        this.renderActiveFilters();
        this.syncSortControls(sortKey);
        this.updateSeoState(url);
        container?.removeAttribute('aria-busy');
        this.locationStateSignature = this.getLocationStateSignature();
        const duration = Math.round((performance.now() - startedAt) * 10) / 10;
        this.dataset.lastResponseMs = String(duration);
        this.dataset.renderState = 'ready';
        document.dispatchEvent(new CustomEvent('oe:filters:updated', {
          detail: { count, duration, state: this.adapter.cloneState(this.state) },
        }));
        if (announce) this.announce(count + ' Produkte gefunden.');
        if (constrained && Number(url.searchParams.get('page')) > 1) {
          container?.scrollIntoView({ block: 'start' });
        }
      } catch (error) {
        if (error.name === 'AbortError' || token !== this.renderToken) return;
        console.error('Ofenerlebnis Produktseite:', error);
        this.dataset.renderState = 'failed';
        container?.removeAttribute('aria-busy');
        if (constrained && container) {
          container.innerHTML = '<p role="status">Produkte konnten nicht geladen werden. <a href="' + this.escape(window.location.href) + '">Seite neu laden</a></p>';
          this.nativeGridVisible = false;
        }
        this.announce('Produkte konnten nicht geladen werden. Bitte Seite neu laden.');
        if (this.dataset.hydrationState !== 'ready') throw error;
      }
    }

    updateCounts(resultCount) {
      const counts = this.adapter.getCounts(this.state);
      const productCount = document.querySelector('#ProductCount');
      if (productCount) {
        const total = this.adapter.products.length + (this.verifiedPowerSelectionActive ? 0 : this.unindexedHandles.length);
        productCount.dataset.productCount = String(resultCount);
        productCount.dataset.totalCount = String(total);
        productCount.textContent =
          resultCount === total
            ? `${resultCount} Produkte`
            : `${resultCount} von ${total} Produkten`;
      }
      this.querySelectorAll('[data-oe-filter-form="mobile"]').forEach((form) => this.updateMobileApplyButton(form));
      this.querySelectorAll('[data-oe-filter-group]').forEach((group) => {
        const handle = group.dataset.filterHandle;
        group.querySelectorAll('[data-oe-value]').forEach((input) => {
          const count = counts[handle]?.[input.value] ?? 0;
          const item = input.closest('[data-oe-filter-value]');
          const disabled = count === 0 && !input.checked;
          input.disabled = disabled;
          item.classList.toggle('is-disabled', disabled);
        });
      });
    }

    syncForms() {
      this.querySelectorAll('[data-oe-filter-group]').forEach((group) => {
        const handle = group.dataset.filterHandle;
        const selected = this.state.values[handle] || [];
        group.querySelectorAll('[data-oe-value]').forEach((input) => {
          input.checked = selected.includes(input.value);
          input.closest('[data-oe-filter-value]').classList.toggle('is-active', input.checked);
        });
        const range = group.querySelector('[data-oe-range]');
        if (!range) return;
        const factor = Number(range.dataset.factor) || 1;
        const selectedRange = this.state.ranges[handle] || {};
        const minSlider = range.querySelector('[data-oe-range-min]');
        const maxSlider = range.querySelector('[data-oe-range-max]');
        const minNumber = range.querySelector('[data-oe-number-min]');
        const maxNumber = range.querySelector('[data-oe-number-max]');
        const minDisplay = selectedRange.min == null ? null : selectedRange.min / factor;
        const maxDisplay = selectedRange.max == null ? null : selectedRange.max / factor;
        minNumber.value = minDisplay == null ? '' : this.formatNumericInput(minDisplay, handle);
        maxNumber.value = maxDisplay == null ? '' : this.formatNumericInput(maxDisplay, handle);
        if (minSlider) minSlider.value = minDisplay == null ? range.dataset.displayMin : String(minDisplay);
        if (maxSlider) maxSlider.value = maxDisplay == null ? range.dataset.displayMax : String(maxDisplay);
        this.updateRangeProgress(range);
      });
    }

    renderActiveFilters() {
      const container = document.querySelector('[data-oe-active-filters]');
      const chips = container?.querySelector('[data-oe-active-filter-chips]');
      const entries = [];
      for (const handle of Object.keys(this.adapter.dimensions)) {
        for (const value of this.state.values[handle] || []) {
          entries.push({ handle, value, label: `${this.labels.get(handle)}: ${value}` });
        }
        const range = this.state.ranges[handle];
        if (range) entries.push({ handle, value: '', label: this.rangeChipLabel(handle, range) });
      }
      this.querySelectorAll('[data-oe-sidebar-reset]').forEach((button) => {
        button.hidden = entries.length === 0;
      });
      if (!container || !chips) return;
      container.hidden = entries.length === 0;
      chips.innerHTML = entries
        .map(
          (entry) => `<span role="listitem"><button class="oe-active-filters__chip" type="button" data-oe-remove-handle="${this.escape(
            entry.handle,
          )}" data-oe-remove-value="${this.escape(entry.value)}">${this.escape(entry.label)}<span aria-hidden="true">×</span><span class="visually-hidden">Filter entfernen</span></button></span>`,
        )
        .join('');
    }

    rangeChipLabel(handle, range) {
      const dimension = this.adapter.dimensions[handle];
      const unit = dimension.du || dimension.u || '';
      const factor = dimension.f || 1;
      const format = (value) => this.numberFormatter.format(value / factor);
      if (handle !== 'preis') {
        const suffix = unit ? ` ${unit}` : '';
        if (range.min != null && range.max != null) {
          return `${this.labels.get(handle)}: ${format(range.min)}–${format(range.max)}${suffix}`;
        }
        if (range.min != null) return `${this.labels.get(handle)}: ab ${format(range.min)}${suffix}`;
        return `${this.labels.get(handle)}: bis ${format(range.max)}${suffix}`;
      }
      const formatMoney = (value) => this.moneyFormatter.format(value / factor);
      if (range.min != null && range.max != null) {
        return `${this.labels.get(handle)}: ${formatMoney(range.min)} – ${formatMoney(range.max)}`;
      }
      if (range.min != null) return `${this.labels.get(handle)}: ab ${formatMoney(range.min)}`;
      return `${this.labels.get(handle)}: bis ${formatMoney(range.max)}`;
    }

    captureStandardCards(root) {
      const handles = [];
      root.querySelectorAll('[data-collection-discovery-item][data-product-handle]').forEach((card) => {
        const handle = card.dataset.productHandle;
        if (handle) handles.push(handle);
        if (handle && !this.cardCache.has(handle)) this.cardCache.set(handle, card.outerHTML);
      });
      return handles;
    }

    async fetchProductCard(handle) {
      if (this.cardPromises.has(handle)) return this.cardPromises.get(handle);
      const request = (async () => {
        const url = new URL('/products/' + encodeURIComponent(handle), window.location.origin);
        url.searchParams.set('view', 'oe-collection-card');
        const response = await fetch(url, { cache: 'no-store', signal: this.signal });
        if (!response.ok) throw new Error('Produktkarte: HTTP ' + response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const card = doc.querySelector('[data-collection-discovery-item][data-product-handle]');
        if (!card || card.dataset.productHandle !== handle) throw new Error('Standard-Produktkarte fehlt: ' + handle);
        return card.outerHTML;
      })().finally(() => this.cardPromises.delete(handle));
      this.cardPromises.set(handle, request);
      return request;
    }

    async ensureStandardCards(products, token = this.renderToken) {
      const wanted = new Set(products.map(product => product.h));
      // A bounded current-page cache, not the full Collection card catalogue.
      for (const handle of this.cardCache.keys()) if (!wanted.has(handle)) this.cardCache.delete(handle);
      const missing = products.filter(product => !this.cardCache.has(product.h));
      for (let start = 0; start < missing.length; start += 4) {
        if (token !== this.renderToken) return;
        const cards = await Promise.all(missing.slice(start, start + 4).map(async product => [product.h, await this.fetchProductCard(product.h)]));
        if (token !== this.renderToken) return;
        for (const [handle, html] of cards) this.cardCache.set(handle, html);
      }
    }

    async renderGrid(products, page, pages, token = ++this.renderToken) {
      const start = (page - 1) * this.pageSize;
      const pageProducts = products.slice(start, start + this.pageSize);
      await this.ensureStandardCards(pageProducts, token);
      if (token === this.renderToken) this.commitGrid(pageProducts, page, pages);
    }

    commitGrid(products, page, pages) {
      const container = document.querySelector('#ProductGridContainer');
      if (!container) throw new Error('Produktgrid-Container fehlt.');
      const wrapper = document.createElement('div');
      wrapper.className = 'collection oe-index-results';
      wrapper.dataset.oeIndexResults = '';
      if (!products.length) {
        wrapper.innerHTML = this.context === 'search'
          ? '<div class="oe-search-empty" role="status"><p><strong>Nichts gefunden?</strong> Dann lassen Sie sich von unseren Experten beraten. Über <a href="/pages/kontakt">Beratung & Kontakt</a> beantworten wir Ihre Fragen rund um Kamin und Ofen. Alternativ können Sie mit unserem <a href="/pages/produktfinder">Produktfinder</a> passende Produkte entdecken.</p><p><strong>Sie haben bereits eine Feuerstätte?</strong> Im <a href="/blogs/ratgeber">Ratgeber</a> finden Sie Wissenswertes zur Auswahl, Nutzung und Pflege Ihres Kamins oder Ofens.</p></div>'
          : '<div class="collection collection--empty page-width"><div class="title-wrapper center"><p class="title title--primary">Keine Produkte entsprechen dieser Auswahl.</p></div></div>';
      } else {
        const grid = document.createElement('ul');
        grid.id = 'product-grid';
        grid.className = this.gridClassName;
        products.forEach(product => grid.append(this.standardCard(product)));
        wrapper.append(grid);
        wrapper.insertAdjacentHTML('beforeend', this.pagination(page, pages));
      }
      container.replaceChildren(wrapper);
      this.dataset.cardRenderer = 'collection-standard';
    }

    standardCard(product) {
      const template = document.createElement('template');
      template.innerHTML = this.cardCache.get(product.h).trim();
      const card = template.content.firstElementChild;
      card.hidden = false;
      card.classList.add('scroll-trigger--cancel');
      if (this.adapter.hasAnyConstraint(this.state)) {
        const variant = this.adapter.selectVariant(product, this.state);
        if (variant) {
          card.querySelectorAll('a[href*="/products/"]').forEach((anchor) => {
            const url = new URL(anchor.getAttribute('href'), window.location.origin);
            url.searchParams.set('variant', variant.id);
            anchor.href = url.pathname + url.search;
          });
          if (product.p?.cv === 1 && variant.cv) this.applyCarrezCardVariant(card, variant);
        }
      }
      return card;
    }

    applyCarrezCardVariant(card, variant) {
      const data = variant.cv;
      const exact = data.mediaColor === data.code && Boolean(variant.i);
      const source = exact ? variant.i : data.modelImage;
      card.dataset.selectedVariantId = variant.id;
      const price = card.querySelector('.oe-collection-card__prices .price');
      if (price && Number.isFinite(variant.pr)) {
        const sale = Number.isFinite(variant.cp) && variant.cp > variant.pr;
        price.classList.toggle('price--on-sale', sale);
        price.classList.toggle('price--sold-out', variant.a === 0);
        const regular = price.querySelector('.price__regular .price-item--regular');
        const current = price.querySelector('.price__sale .price-item--sale');
        const previous = price.querySelector('.price__sale .price-item--regular');
        if (regular) regular.textContent = this.moneyFormatter.format(variant.pr);
        if (current) current.textContent = this.moneyFormatter.format(variant.pr);
        if (previous) previous.textContent = sale ? this.moneyFormatter.format(variant.cp) : '';
      }
      card.querySelectorAll('[data-oe-wishlist-button]').forEach(button => { button.dataset.variantId = variant.id; });
      const image = card.querySelector('.oe-collection-card__image img');
      if (image && source) {
        image.removeAttribute('srcset');
        image.src = source;
        image.alt = exact ? `${data.size} · ${data.label}` : `Modellabbildung ${data.size}; gewählte Farbe am Bild nicht belegt`;
      }
      // A model gallery has no evidence for all 191 colours. Freeze it to the
      // selected SKU image rather than allowing unlabelled colour changes.
      card.querySelectorAll('[data-card-image-step], .oe-collection-card__pagination, [data-card-images]').forEach(element => element.remove());
      const gallery = card.querySelector('oe-card-gallery');
      if (gallery) gallery.dataset.imageCount = '1';
    }

    pagination(page, pages) {
      if (pages <= 1) return '';
      const items = [];
      const link = (number, label = String(number), extra = '') => {
        if (number === page && !extra) return '<li><span class="oe-pagination__item is-current" aria-current="page">' + number + '</span></li>';
        const url = new URL(window.location.href);
        if (number === 1) url.searchParams.delete('page');
        else url.searchParams.set('page', String(number));
        url.hash = this.serializeState(this.state);
        const neighbor = !extra && page > 3 && page < pages - 2 && number !== 1 && number !== pages ? ' oe-pagination__neighbor' : '';
        return '<li class="' + neighbor.trim() + '"><a class="oe-pagination__item ' + extra + '" href="' + this.escape(url.pathname + url.search + url.hash) + '" aria-label="' + this.escape(extra ? label : 'Seite ' + number) + '">' + (extra ? (number < page ? '‹' : '›') : number) + '</a></li>';
      };
      const dots = '<li><span class="oe-pagination__ellipsis" aria-hidden="true">…</span></li>';
      if (page > 1) items.push(link(page - 1, 'Zurück', 'oe-pagination__arrow'));
      items.push(link(1));
      const start = page <= 2 ? 2 : Math.max(2, page >= pages - 1 ? pages - 2 : page - 1);
      const end = Math.min(pages - 1, page <= 2 ? 3 : page + 1);
      if (start > 2) items.push(dots);
      for (let number = start; number <= end; number += 1) items.push(link(number));
      if (end < pages - 1) items.push(dots);
      items.push(link(pages));
      if (page < pages) items.push(link(page + 1, 'Weiter', 'oe-pagination__arrow'));
      return '<nav class="oe-pagination" aria-label="Seitennavigation"><ul class="oe-pagination__list" role="list">' + items.join('') + '</ul></nav>';
    }

    onChange(event) {
      const form = event.target.closest('[data-oe-filter-form]');
      if (!form) return;
      if (event.target.matches('[data-oe-value]')) {
        const group = event.target.closest('[data-oe-filter-group]');
        if (group.dataset.multiSelect === 'false' && event.target.checked) {
          group.querySelectorAll('[data-oe-value]').forEach((input) => {
            if (input !== event.target) input.checked = false;
          });
        }
      }
      if (event.target.matches('[data-oe-number-min], [data-oe-number-max]')) {
        this.syncRangeFromNumber(event.target.closest('[data-oe-range]'), event.target);
      }
      if (form.dataset.oeFilterForm === 'desktop') this.scheduleApply(form, 120);
      else this.updateMobileApplyButton(form);
    }

    onInput(event) {
      if (event.target.matches('[data-oe-filter-search]')) {
        this.filterVisibleValues(event.target);
        return;
      }
      if (!event.target.matches('[data-oe-range-min], [data-oe-range-max]')) return;
      const range = event.target.closest('[data-oe-range]');
      this.syncRangeFromSlider(range, event.target);
      const form = event.target.closest('[data-oe-filter-form]');
      if (form?.dataset.oeFilterForm === 'desktop') this.scheduleApply(form, 300);
      else if (form) this.updateMobileApplyButton(form);
    }

    updateMobileApplyButton(form) {
      const button = form?.querySelector('.oe-filter-drawer__apply');
      if (!button) return;
      const stagedState = this.stateFromForm(form);
      const selectedValues = Object.values(stagedState.values).reduce((sum, values) => sum + values.length, 0);
      const selectedRanges = Object.keys(stagedState.ranges).length;
      const selectionCount = selectedValues + selectedRanges;
      const hasPendingChanges = this.serializeState(stagedState) !== this.serializeState(this.state || this.emptyState());
      button.textContent = selectionCount > 0 ? `Filter anwenden (${selectionCount})` : 'Filter anwenden';
      button.dataset.selectionCount = String(selectionCount);
      button.dataset.pendingChanges = String(hasPendingChanges);
      button.disabled = !hasPendingChanges;
    }

    onSubmit(event) {
      const form = event.target.closest('[data-oe-filter-form]');
      if (!form) return;
      event.preventDefault();
      this.closeDrawer(false);
      this.applyForm(form);
    }

    onClick(event) {
      const open = event.target.closest('[data-oe-drawer-open]');
      if (open) return this.openDrawer(open);
      if (event.target.closest('[data-oe-drawer-close]')) return this.closeDrawer(true);
      const showMore = event.target.closest('[data-oe-show-more]');
      if (showMore) return this.toggleVisibleValues(showMore);
      if (this.dataset.adapterReady !== 'true') return;
      if (event.target.closest('[data-oe-reset]')) return this.reset();
      const remove = event.target.closest('[data-oe-remove-handle]');
      if (remove) return this.removeFilter(remove.dataset.oeRemoveHandle, remove.dataset.oeRemoveValue);
    }

    onDocumentChange(event) {
      if (!event.target.matches('#SortBy, #SortBy-mobile, [data-oe-sort]')) return;
      const url = new URL(window.location.href);
      if (event.target.value && event.target.value !== this.defaultSort) url.searchParams.set('sort_by', event.target.value);
      else url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
      if (this.context === 'collection' && (!this.state || !this.hasFilterState()) && !this.verifiedPowerSelectionActive) {
        window.location.assign(url.pathname + url.search + '#ProductGridContainer');
        return;
      }
      window.history.pushState({ oeFilterState: true }, '', url);
      this.render({ announce: true });
    }

    onPopstate() {
      if (this.locationStateSignature === this.getLocationStateSignature()) return;
      this.state = this.readStateFromLocation();
      this.render({ announce: true });
    }

    getLocationStateSignature() {
      const url = new URL(window.location.href);
      const filterHash = url.hash.startsWith(HASH_PREFIX) ? url.hash : '';
      return `${url.searchParams.get('sort_by') || this.defaultSort}|${url.searchParams.get('page') || '1'}|${filterHash}`;
    }

    onKeydown(event) {
      const drawer = this.querySelector('[data-oe-drawer]:not([hidden])');
      if (!drawer) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = this.focusableElements(drawer);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    applyForm(form) {
      this.state = this.stateFromForm(form);
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.history.pushState({ oeFilterState: true }, '', url.pathname + url.search + this.serializeState(this.state));
      this.render({ announce: true });
    }

    scheduleApply(form, delay) {
      window.clearTimeout(this.applyTimer);
      this.applyTimer = window.setTimeout(() => this.applyForm(form), delay);
    }

    reset() {
      this.state = this.emptyState();
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.hash = '';
      window.history.pushState({ oeFilterState: true }, '', url);
      this.closeDrawer(false);
      this.render({ announce: true });
      this.querySelector('[data-oe-drawer-open]')?.focus({ preventScroll: true });
    }

    removeFilter(handle, value) {
      if (value) {
        this.state.values[handle] = (this.state.values[handle] || []).filter((entry) => entry !== value);
        if (!this.state.values[handle].length) delete this.state.values[handle];
      } else delete this.state.ranges[handle];
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.history.pushState({ oeFilterState: true }, '', url.pathname + url.search + this.serializeState(this.state));
      this.render({ announce: true });
    }

    syncSortControls(sortKey) {
      document.querySelectorAll('#SortBy, #SortBy-mobile, [data-oe-sort]').forEach((select) => {
        if ([...select.options].some((option) => option.value === sortKey)) select.value = sortKey;
      });
    }

    syncRangeFromSlider(range, source) {
      if (!range) return;
      const minSlider = range.querySelector('[data-oe-range-min]');
      const maxSlider = range.querySelector('[data-oe-range-max]');
      const minNumber = range.querySelector('[data-oe-number-min]');
      const maxNumber = range.querySelector('[data-oe-number-max]');
      if (Number(minSlider.value) > Number(maxSlider.value)) {
        if (source === minSlider) minSlider.value = maxSlider.value;
        else maxSlider.value = minSlider.value;
      }
      const handle = range.dataset.handle;
      minNumber.value = this.formatNumericInput(Number(minSlider.value), handle);
      maxNumber.value = this.formatNumericInput(Number(maxSlider.value), handle);
      this.updateRangeProgress(range);
    }

    syncRangeFromNumber(range, source) {
      if (!range) return;
      const minSlider = range.querySelector('[data-oe-range-min]');
      const maxSlider = range.querySelector('[data-oe-range-max]');
      const minNumber = range.querySelector('[data-oe-number-min]');
      const maxNumber = range.querySelector('[data-oe-number-max]');
      const displayMin = Number(range.dataset.displayMin);
      const displayMax = Number(range.dataset.displayMax);
      const handle = range.dataset.handle;
      const parsed = this.parseLocalizedNumber(source.value);
      if (!minSlider || !maxSlider) {
        if (source.value === '' || !Number.isFinite(parsed)) return;
        const otherInput = source === minNumber ? maxNumber : minNumber;
        const otherParsed = this.parseLocalizedNumber(otherInput.value);
        const boundary = Number.isFinite(otherParsed)
          ? otherParsed
          : source === minNumber
            ? displayMax
            : displayMin;
        const normalized = source === minNumber
          ? Math.min(Math.max(parsed, displayMin), boundary)
          : Math.max(Math.min(parsed, displayMax), boundary);
        source.value = this.formatNumericInput(normalized, handle);
        return;
      }
      if (source === minNumber) {
        minSlider.value = source.value === '' || !Number.isFinite(parsed)
          ? displayMin
          : Math.min(Math.max(parsed, displayMin), Number(maxSlider.value));
        if (source.value !== '' && Number.isFinite(parsed)) source.value = this.formatNumericInput(Number(minSlider.value), handle);
      } else {
        maxSlider.value = source.value === '' || !Number.isFinite(parsed)
          ? displayMax
          : Math.max(Math.min(parsed, displayMax), Number(minSlider.value));
        if (source.value !== '' && Number.isFinite(parsed)) source.value = this.formatNumericInput(Number(maxSlider.value), handle);
      }
      this.updateRangeProgress(range);
    }

    updateRangeProgress(range) {
      const minSlider = range.querySelector('[data-oe-range-min]');
      const maxSlider = range.querySelector('[data-oe-range-max]');
      if (!minSlider || !maxSlider) return;
      const min = Number(minSlider.min);
      const max = Number(minSlider.max);
      const span = max - min || 1;
      range.style.setProperty('--oe-range-start', `${((Number(minSlider.value) - min) / span) * 100}%`);
      range.style.setProperty('--oe-range-end', `${((Number(maxSlider.value) - min) / span) * 100}%`);
    }

    filterVisibleValues(input) {
      const group = input.closest('[data-oe-filter-group]');
      const term = input.value.trim().toLocaleLowerCase('de');
      const values = group.querySelector('[data-oe-filter-values]');
      if (values) values.dataset.oeSearching = String(Boolean(term));
      const showMore = group.querySelector('[data-oe-show-more]');
      if (showMore) showMore.hidden = Boolean(term);
      group.querySelectorAll('[data-oe-filter-value]').forEach((item) => {
        const matches = (item.dataset.filterLabel || '').toLocaleLowerCase('de').includes(term);
        item.hidden = !matches;
      });
    }

    toggleVisibleValues(button) {
      const values = button.closest('[data-oe-filter-control]')?.querySelector('[data-oe-filter-values]');
      if (!values) return;
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';
      if (expanded) values.dataset.oeExpanded = 'true';
      else delete values.dataset.oeExpanded;
    }

    updateSeoState(url) {
      if (this.context === 'search') {
        let robots = document.querySelector('meta[name="robots"]');
        if (!robots) {
          robots = document.createElement('meta');
          robots.name = 'robots';
          document.head.append(robots);
        }
        robots.content = 'noindex,follow';
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) {
          const canonicalUrl = new URL(this.baseUrl, window.location.origin);
          canonicalUrl.searchParams.delete('page');
          canonicalUrl.searchParams.delete('sort_by');
          canonicalUrl.searchParams.delete('section_id');
          canonical.href = canonicalUrl.toString();
        }
        return;
      }
      const hasUserState = this.hasFilterState() || url.searchParams.has('sort_by') || this.verifiedPowerSelectionActive || [...url.searchParams.keys()].some(key => key.startsWith('filter.'));
      let robots = document.querySelector('meta[name="robots"]');
      if (hasUserState || url.searchParams.has('page')) {
        if (!robots) {
          robots = document.createElement('meta');
          robots.name = 'robots';
          document.head.append(robots);
        }
        robots.content = 'noindex,follow';
      } else if (robots) robots.content = 'index,follow';
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        const canonicalUrl = new URL(this.baseUrl, window.location.origin);
        if (!hasUserState && url.searchParams.has('page')) canonicalUrl.searchParams.set('page', url.searchParams.get('page'));
        canonical.href = canonicalUrl.toString();
      }
    }

    openDrawer(opener) {
      const drawer = this.querySelector('[data-oe-drawer]');
      if (!drawer) return;
      this.drawerOpener = opener;
      drawer.hidden = false;
      document.body.classList.add('oe-filter-drawer-open');
      window.requestAnimationFrame(() => drawer.querySelector('[data-oe-drawer-panel]')?.focus());
    }

    closeDrawer(returnFocus) {
      const drawer = this.querySelector('[data-oe-drawer]');
      if (!drawer || drawer.hidden) return;
      drawer.hidden = true;
      document.body.classList.remove('oe-filter-drawer-open');
      if (returnFocus) this.drawerOpener?.focus();
    }

    focusableElements(container) {
      return [...container.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
        (element) => !element.hidden && element.offsetParent !== null,
      );
    }

    cleanNumber(value) {
      return String(Math.round((Number(value) + Number.EPSILON) * 100) / 100);
    }

    parseLocalizedNumber(value) {
      const normalized = String(value || '')
        .trim()
        .replace(/\s|€/g, '')
        .replace(/\.(?=\d{3}(?:\D|$))/g, '')
        .replace(',', '.');
      if (!normalized) return Number.NaN;
      const number = Number(normalized);
      return Number.isFinite(number) ? number : Number.NaN;
    }

    formatNumericInput(value, handle) {
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: handle === 'preis' ? 2 : 1,
      }).format(Number(value));
    }

    announce(message) {
      const status = this.querySelector('[data-oe-filter-status]');
      if (status) status.textContent = message;
    }

    failSafely(error) {
      console.error('Ofenerlebnis independent filter engine:', error);
      this.dataset.adapterReady = 'false';
      this.dataset.hydrationState = 'failed';
      this.setAttribute('aria-busy', 'false');
      this.announce('Filter stehen derzeit nicht zur Verfügung. Das Produktgrid bleibt verfügbar.');
    }

    escape(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  customElements.define('ofenerlebnis-filter-engine', OfenerlebnisFilterEngine);
})();
